import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Empty from 'antd/lib/empty';
import Spin from 'antd/lib/spin';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import { useNavigate } from 'react-router-dom';
import IconBadge from '../helpdesk/IconBadge';

function ArticlesSection({
  articles,
  statusOptions,
  categoryOptions,
  tagOptions,
  searchValue,
  statusValue,
  categoryValue,
  tagValue,
  loading,
  onSearchChange,
  onStatusChange,
  onCategoryChange,
  onTagChange,
  onDeleteArticle,
  deletingArticleId,
  totalCount,
}) {
  const navigate = useNavigate();
  const { Search } = Input;
  const showDeleteConfirm = (article) => {
    Modal.confirm({
      title: 'Delete article',
      content: 'Are you sure you want to delete this article?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => onDeleteArticle(article),
    });
  };

  const getArticleMenuItems = (article) => [
    {
      key: 'edit',
      label: 'Edit',
    },
    {
      key: 'preview',
      label: 'Preview',
      disabled: article.status !== 'Published',
    },
    {
      key: 'delete',
      label: 'Delete',
      danger: true,
    },
  ];

  const onArticleMenuClick = (article, event) => {
    if (event.key === 'edit') {
      navigate(`/knowledge-base/edit/${article.id}`);
      return;
    }
    if (event.key === 'preview') {
      navigate(`/articles/${encodeURIComponent(article.slug || article.id)}`);
      return;
    }
    if (event.key === 'delete') {
      showDeleteConfirm(article);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => (
        <button
          type="button"
          className="kb-table__title"
          onClick={() => navigate(`/knowledge-base/edit/${record.id}`)}
          title={record.title}
        >
          <span>{record.title}</span>
        </button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value, record) => (
        <span className={`kb-pill kb-pill--${record.statusTone}`.trim()}>{value}</span>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 220,
      render: (tags) =>
        tags?.length ? (
          <span className="kb-table__tags">
            <Tag key={tags[0]}>{tags[0]}</Tag>
            {tags.length > 1 ? <Tag className="kb-table__tag-overflow">+{tags.length - 1}</Tag> : null}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      width: 170,
      ellipsis: true,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 130,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, article) => (
        <div className="kb-table__actions">
          <Button size="small" onClick={() => navigate(`/knowledge-base/edit/${article.id}`)}>
            Edit
          </Button>
          <Button
            size="small"
            onClick={() => navigate(`/articles/${encodeURIComponent(article.slug || article.id)}`)}
            disabled={article.status !== 'Published'}
          >
            Preview
          </Button>
          <Popconfirm
            title="Delete article"
            description="Are you sure you want to delete this article?"
            onConfirm={() => onDeleteArticle(article)}
            okText="Delete"
            okButtonProps={{ danger: true, loading: deletingArticleId === article.id }}
            cancelButtonProps={{ disabled: deletingArticleId === article.id }}
          >
            <Button danger size="small" loading={deletingArticleId === article.id}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <section className="kb-articles" aria-label="Articles list">
      <div className="kb-articles__header">
        <h2>Article Library</h2>
        <p>{totalCount} total</p>
      </div>

      <div className="kb-articles__toolbar">
        <Search
          placeholder="Search articles..."
          enterButton={false}
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
        />
        <div className="kb-articles__filter-icon" aria-hidden="true">
          <IconBadge icon="filter" />
        </div>
        <Select value={statusValue} options={statusOptions} onChange={onStatusChange} />
        <Select value={categoryValue} options={categoryOptions} onChange={onCategoryChange} />
        <Select value={tagValue} options={tagOptions} onChange={onTagChange} />
      </div>

      <div className="kb-articles__grid">
        {loading ? (
          <div className="kb-articles__loading">
            <Spin />
          </div>
        ) : articles.length ? (
          <>
            <Table
              className="kb-table"
              rowKey={(record) => record.id}
              columns={columns}
              dataSource={articles}
              scroll={{ x: 1100 }}
              pagination={{ pageSize: 8 }}
            />
            <div className="kb-table-mobile" aria-label="Articles mobile list">
              {articles.map((article) => (
                <article key={article.id} className="kb-mobile-card">
                  <div className="kb-mobile-card__head">
                    <button
                      type="button"
                      className="kb-mobile-card__title"
                      onClick={() => navigate(`/knowledge-base/edit/${article.id}`)}
                      title={article.title}
                    >
                      {article.title}
                    </button>
                    <div className="kb-mobile-card__head-right">
                      <span className={`kb-pill kb-pill--${article.statusTone}`.trim()}>{article.status}</span>
                      <Dropdown
                        menu={{
                          items: getArticleMenuItems(article),
                          onClick: (event) => onArticleMenuClick(article, event),
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                      >
                        <button type="button" className="kb-mobile-card__menu-trigger" aria-label="Article actions">
                          <IconBadge icon="more" />
                        </button>
                      </Dropdown>
                    </div>
                  </div>
                  <p className="kb-mobile-card__text">{article.description || '-'}</p>
                  <div className="kb-mobile-card__meta">
                    <span>{article.category || '-'}</span>
                    <span>{article.author || '-'}</span>
                    <span>{article.date || '-'}</span>
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <Empty description="No articles found" />
        )}
      </div>
    </section>
  );
}

export default ArticlesSection;
